<div class="filter-page">
    <!-- Loading State -->
    <div class="filter-loading" *ngIf="isLoading">
        <mat-spinner diameter="40"></mat-spinner>
        <p>Loading filters...</p>
    </div>

    <!-- Error State -->
    <div class="filter-error" *ngIf="hasError && !isLoading">
        <mat-icon>error_outline</mat-icon>
        <p>Unable to load filters</p>
        <button mat-button (click)="closeFilter()">Close</button>
    </div>

    <!-- No Filters Available -->
    <div class="filter-empty" *ngIf="!isLoading && !hasError && !currentFilters">
        <mat-icon>filter_list_off</mat-icon>
        <p>No filters available</p>
        <button mat-button (click)="closeFilter()">Close</button>
    </div>

    <!-- Filter Content -->
    <div class="filter-body" *ngIf="!isLoading && !hasError && currentFilters">
        
        <!-- Filter Panel -->
        <div class="filter-panel">
            <!-- Close Button -->
            <div class="filter-section">
                <button class="close-button" (click)="closeFilter()">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M6 6L18 18M18 6L6 18" stroke="#888888" stroke-width="2" stroke-linecap="round"/>
                    </svg>
                </button>
            </div>
            
            <!-- Provider Name Search -->
            <div class="filter-section">
                <div class="filter-section-title">{{providerLabel}}</div>
                <div class="filter-search-input">
                    <mat-icon class="search-icon">search</mat-icon>
                    <input type="text" 
                           [placeholder]="searchPlaceholderText" 
                           [(ngModel)]="providerNameText"
                           (ngModelChange)="filterProviderNames($event)"
                           [matAutocomplete]="providerNameAuto">
                    <mat-autocomplete #providerNameAuto="matAutocomplete">
                        <mat-option *ngFor="let option of filteredProviderOptions$ | async" [value]="option">
                            {{option}}
                        </mat-option>
                    </mat-autocomplete>
                </div>
            </div>

            <!-- Specialty Filter with Autocomplete -->
            <div class="filter-section" *ngIf="currentFilters && currentFilters.specialtyList && currentFilters.specialtyList.length > 0">
                <div class="filter-section-title">
                    {{specialtyLabel}} ({{specialtyCount}})
                </div>
                
                <!-- Autocomplete Search -->
                <div class="filter-search-input">
                    <mat-icon class="search-icon">search</mat-icon>
                    <input type="text" 
                           [placeholder]="searchPlaceholderText"
                           [(ngModel)]="specialtySearchText"
                           (ngModelChange)="filterSpecialties($event)"
                           [matAutocomplete]="specialtyAuto">
                    <mat-autocomplete #specialtyAuto="matAutocomplete"
                                      (optionSelected)="onSpecialtySelected($event)">
                        <mat-option *ngFor="let specialty of (filteredSpecialties$ | async)" 
                                    [value]="specialty.value">
                            {{specialty.value}}
                        </mat-option>
                    </mat-autocomplete>
                </div>

                <!-- Selected Specialties as Chips -->
                <div class="filter-chips" *ngIf="selectedSpecialties.length > 0">
                    <mat-chip-set>
                        <mat-chip *ngFor="let specialty of selectedSpecialties" 
                                  (removed)="removeSpecialty(specialty)"
                                  [removable]="true">
                            {{specialty.value}}
                            <button matChipRemove>
                                <mat-icon>cancel</mat-icon>
                            </button>
                        </mat-chip>
                    </mat-chip-set>
                </div>
            </div>

            <!-- Online Scheduling Filter -->
            <div class="filter-section" *ngIf="currentFilters && currentFilters.onlineSchedulingList && currentFilters.onlineSchedulingList.length > 0">
                <div class="filter-section-title">{{onlineSchedLabel}}</div>
                <div class="filter-options">
                    <div *ngFor="let option of currentFilters.onlineSchedulingList" class="filter-option">
                        <mat-checkbox 
                            [(ngModel)]="option.isSelected">
                            {{option.value}}
                        </mat-checkbox>
                    </div>
                </div>
            </div>

            <!-- Affiliation Filter -->
            <div class="filter-section" *ngIf="currentFilters && currentFilters.affiliationList && currentFilters.affiliationList.length > 0">
                <div class="filter-section-title">
                    {{medGroupLabel}} ({{getSelectedCount(currentFilters.affiliationList)}})
                </div>
                <div class="filter-options">
                    <div *ngFor="let option of currentFilters.affiliationList" class="filter-option">
                        <mat-checkbox 
                            [(ngModel)]="option.isSelected">
                            {{option.value}}
                        </mat-checkbox>
                    </div>
                </div>
            </div>

            <!-- Gender Filter -->
            <div class="filter-section" *ngIf="currentFilters && currentFilters.genderList && currentFilters.genderList.length > 0">
                <div class="filter-section-title">{{genderLabel}}</div>
                <div class="filter-options-horizontal">
                    <div *ngFor="let option of currentFilters.genderList" class="filter-option">
                        <mat-checkbox 
                            [(ngModel)]="option.isSelected"
                            (change)="onGenderChange(option)">
                            {{option.value}}
                        </mat-checkbox>
                    </div>
                </div>
            </div>

            <!-- Languages Filter with Autocomplete -->
            <div class="filter-section" *ngIf="currentFilters && currentFilters.languageList && currentFilters.languageList.length > 0">
                <div class="filter-section-title">
                    {{langLabel}} ({{languageCount}})
                </div>

                <!-- Autocomplete Search for Additional Languages -->
                <div class="filter-search-input">
                    <mat-icon class="search-icon">search</mat-icon>
                    <input type="text" 
                           [placeholder]="searchPlaceholderText"
                           [(ngModel)]="languageSearchText"
                           (ngModelChange)="filterLanguages($event)"
                           [matAutocomplete]="languageAuto">
                    <mat-autocomplete #languageAuto="matAutocomplete"
                                      (optionSelected)="onLanguageSelected($event)">
                        <mat-option *ngFor="let language of (filteredLanguages$ | async)" 
                                    [value]="language.value">
                            {{language.value}}
                        </mat-option>
                    </mat-autocomplete>
                </div>

                <!-- First 5 Languages + Additional Selected as Checkboxes -->
                <div class="filter-options">
                    <div *ngFor="let option of getDisplayedLanguages()" class="filter-option">
                        <mat-checkbox 
                            [checked]="option.isSelected"
                            (change)="onLanguageCheckboxChange(option)">
                            {{option.value}}
                        </mat-checkbox>
                    </div>
                </div>
            </div>

            <!-- Action Buttons -->
            <div class="filter-actions">
                <button mat-button class="clear-button" (click)="clearFilters()">
                    {{clearBtnText}}
                </button>
                <button mat-raised-button class="apply-button" (click)="applyFilters()">
                    {{applyBtnText}}
                </button>
            </div>
        </div>
    </div>
</div>
